import { Link, createFileRoute } from '@tanstack/react-router'
import type { LinkProps } from '@tanstack/react-router'
import { pageMeta } from '~/lib/site/seo'
import { cacheHeaders } from '~/lib/site/cache'
import { bodyClass } from '~/components/ui/BodyText'
import { Container } from '~/components/ui/Container'
import { TextLink } from '~/components/ui/TextLink'
import { DonateSignupBand } from '~/components/sections/DonateSignupBand'
import { Eyebrow } from '~/components/ui/Eyebrow'
import { Img } from '~/components/ui/Img'
import framing from '~/assets/images/veganism-framing.jpg?responsive'
import animals from '~/assets/images/veganism-animals.jpg?responsive'
import march from '~/assets/images/veganism-march.jpg?responsive'
import { PageTitle } from '~/components/ui/PageTitle'
import { PageHero } from '~/components/ui/PageHero'
import { SectionHeading } from '~/components/ui/SectionHeading'

const TITLE = 'Veganism | Direct Action Everywhere'
const DESCRIPTION =
  'From DxE’s perspective, veganism is the way of living that seeks to exclude any products or services derived from the exploitation of nonhuman animals. Learn how DxE goes beyond veganism.'

export const Route = createFileRoute('/veganism')({
  headers: () => cacheHeaders.content,
  head: () => pageMeta({ title: TITLE, description: DESCRIPTION }),
  component: Veganism,
})

const cards: {
  image: typeof framing
  title: string
  body: string
  cta: string
  to: LinkProps
}[] = [
  {
    image: framing,
    title: 'A stronger framing for animal rights',
    body: 'Speaking about speciesism, rather than veganism alone, frames the argument in animals’ favor. Veganism is commonly seen as a lifestyle choice, focused on our individual consumer behavior. DxE focuses on animals, their lives, and our collective liberation as a social justice issue.',
    cta: 'Read more',
    to: {
      to: '/blog/$slug',
      params: {
        slug: '2015-7-27-why-activism-not-veganism-is-the-moral-baseline',
      },
    },
  },
  {
    image: animals,
    title: 'Anti-speciesism addresses more than veganism can',
    body: 'Common formulations of veganism only ask us to reject the most direct products of animal exploitation. This often ignores animals killed in farming processes and urban development, those trapped in zoos and aquariums, and other victims. Anti-speciesism addresses all cases of unnecessary and avoidable violence against animals.',
    cta: 'Read more',
    to: {
      to: '/blog/$slug',
      params: { slug: '2015-1-14-veganism-is-not-cruelty-free' },
    },
  },
  {
    image: march,
    title: 'Building a movement will take more than just veganism',
    body: 'Movement building is a key ingredient in creating long-term systemic change. A focus on promoting veganism only leads to isolated and powerless vegans. We seek to create more than this: a global network of closely connected activists working together on strategic campaigns.',
    cta: 'Join our latest campaign',
    to: {
      to: '/campaigns/$slug',
      params: { slug: 'right-to-rescue' },
    },
  },
]

function Veganism() {
  return (
    <>
      <PageHero>
        <Container width="narrow">
          <Eyebrow>Veganism</Eyebrow>
          <PageTitle size="md" className="mt-2.5 mb-5">
            What is veganism?
          </PageTitle>
          <p className={`${bodyClass} mb-5`}>
            From DxE’s perspective, <em>veganism</em> is the way of living that
            seeks to exclude—as far as is possible and practicable—any products
            or services derived from the exploitation of nonhuman animals. This
            includes any products made from the bodies or secretions of animals,
            as well as those developed using animal testing.
          </p>
          <p className={`${bodyClass} mb-5`}>
            Being vegan is a lot easier when you're part of a community that
            supports you on your journey to help animals.{' '}
            <TextLink href="https://adb.dxe.io/international">
              Join the DxE community!
            </TextLink>
          </p>
          <SectionHeading size="md" className="mt-5 mb-2.5">
            How does DxE go beyond veganism?
          </SectionHeading>
          <p className={`${bodyClass} mb-0`}>
            DxE fights against <em>speciesism</em> in society. Speciesism is the
            set of oppressive ideologies that perpetuates the exploitation of
            nonhuman animals. DxE regards living vegan as one crucial expression
            of anti-speciesist philosophy, but not the only one. To truly oppose
            speciesism, we must take comprehensive direct action to change the
            world for all animals, and living vegan is just part of this.
          </p>
        </Container>
      </PageHero>

      <section className="pt-10 pb-20">
        <Container className="grid gap-[30px] md:grid-cols-3">
          {cards.map((c) => (
            <div key={c.title} className="flex flex-col bg-surface-alt">
              <div className="relative h-[250px] overflow-hidden lg:h-[350px]">
                <Img
                  image={c.image}
                  sizes="(min-width: 768px) 30vw, 90vw"
                  alt=""
                  className="absolute inset-0 h-full w-full object-cover"
                />
              </div>
              <div className="p-[25px]">
                <h2 className="mt-0 mb-2.5 text-[32px] leading-[38px] font-extrabold text-fg md:text-[38px] md:leading-[44px]">
                  {c.title}
                </h2>
                <p className={`${bodyClass} mb-5`}>{c.body}</p>
                <Link
                  {...c.to}
                  className="inline-flex items-center gap-2 text-base text-accent hover:underline"
                >
                  {c.cta}
                  <svg
                    aria-hidden="true"
                    viewBox="0 0 16 12"
                    className="h-3 w-4"
                  >
                    <path
                      d="M0 6h14M9 1l5 5-5 5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                    />
                  </svg>
                </Link>
              </div>
            </div>
          ))}
        </Container>
      </section>

      <DonateSignupBand />
    </>
  )
}
